import { useEffect, useState } from "react";
import { adminApi, apiError } from "@/admin/api";
import { toast } from "sonner";
import { Plus, X, Archive } from "lucide-react";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const EMPTY = { cause: "", title: "", subtitle: "", description: "", goal: 0, raised: 0, location: "", image: "", start_date: "", end_date: "", status: "published" };

export default function Projects() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);

  const load = () => adminApi.get("/admin/projects").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (edit.id) await adminApi.put(`/admin/projects/${edit.id}`, edit);
      else await adminApi.post("/admin/projects", edit);
      toast.success("Saved"); setEdit(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this project? It will be hidden from the public site.")) return;
    await adminApi.delete(`/admin/projects/${id}`); toast.success("Archived"); load();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Projects & Causes</h1>
        <button data-testid="new-project" onClick={() => setEdit({ ...EMPTY })} className="inline-flex items-center gap-2 rounded-lg bg-[hsl(13_65%_54%)] text-white px-4 py-2 text-sm font-medium"><Plus size={15} /> New Project</button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((p) => (
          <div key={p.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`project-${p.id}`}>
            {p.image ? <img src={p.image} alt={p.title} className="h-32 w-full object-cover" /> : <div className="h-32 bg-gray-100" />}
            <div className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">{p.cause}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.status === "published" ? "bg-emerald-100 text-emerald-700" : p.status === "archived" ? "bg-gray-200 text-gray-500" : "bg-amber-100 text-amber-700"}`}>{p.status || "published"}</span>
              </div>
              <h3 className="font-semibold text-gray-900 mt-1">{p.title}</h3>
              <p className="text-xs text-gray-500 mt-1">{inr(p.raised)} / {inr(p.goal)}</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setEdit(p)} className="flex-1 text-sm rounded-lg border border-gray-300 py-1.5">Edit</button>
                <button onClick={() => archive(p.id)} className="rounded-lg border border-gray-300 px-2.5" title="Archive"><Archive size={15} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEdit(null)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} data-testid="project-form" className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto space-y-3">
            <div className="flex items-center justify-between"><h2 className="font-semibold">{edit.id ? "Edit" : "New"} Project</h2><button type="button" onClick={() => setEdit(null)}><X size={18} /></button></div>
            {[["cause", "Cause"], ["title", "Title"], ["subtitle", "Subtitle"], ["location", "Location"], ["image", "Image URL"]].map(([k, l]) => (
              <div key={k}><label className="text-xs text-gray-500">{l}</label><input data-testid={`pf-${k}`} value={edit[k] || ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} className="w-full mt-1 py-2 px-3 rounded-lg border border-gray-300 text-sm" /></div>
            ))}
            <div><label className="text-xs text-gray-500">Description</label><textarea value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} rows={3} className="w-full mt-1 py-2 px-3 rounded-lg border border-gray-300 text-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-gray-500">Goal (₹)</label><input type="number" value={edit.goal} onChange={(e) => setEdit({ ...edit, goal: Number(e.target.value) })} className="w-full mt-1 py-2 px-3 rounded-lg border border-gray-300 text-sm" /></div>
              <div><label className="text-xs text-gray-500">Raised (₹)</label><input type="number" value={edit.raised} onChange={(e) => setEdit({ ...edit, raised: Number(e.target.value) })} className="w-full mt-1 py-2 px-3 rounded-lg border border-gray-300 text-sm" /></div>
              <div><label className="text-xs text-gray-500">Start date</label><input type="date" value={edit.start_date || ""} onChange={(e) => setEdit({ ...edit, start_date: e.target.value })} className="w-full mt-1 py-2 px-3 rounded-lg border border-gray-300 text-sm" /></div>
              <div><label className="text-xs text-gray-500">End date</label><input type="date" value={edit.end_date || ""} onChange={(e) => setEdit({ ...edit, end_date: e.target.value })} className="w-full mt-1 py-2 px-3 rounded-lg border border-gray-300 text-sm" /></div>
            </div>
            <div><label className="text-xs text-gray-500">Status</label>
              <select value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} className="w-full mt-1 py-2 px-3 rounded-lg border border-gray-300 text-sm">
                <option value="published">Published</option><option value="draft">Draft</option><option value="archived">Archived</option>
              </select>
            </div>
            <button data-testid="project-save" className="w-full rounded-lg bg-[hsl(13_65%_54%)] text-white py-2.5 text-sm font-semibold">Save</button>
          </form>
        </div>
      )}
    </div>
  );
}
